import { ChevronDownIcon, ChevronRightIcon, ClipboardCopyIcon, DocumentTextIcon, ExternalLinkIcon, FilterIcon, LightningBoltIcon, PlusIcon, RefreshIcon, UploadIcon } from "@heroicons/react/outline";
import { useTranslation } from "react-i18next";
import learnBg1 from '../assets/learn-bg-1.svg';
import { useModal } from "../contexts";
import FileUploadModal from "./FileUploadModal";
import WorkflowSelectModal from "./WorkflowSelectModal";
import useUser from "../hooks/useUser";
import { deleteData, getData, postData } from "../backend";
import LoadingModal from "./LoadingModal";
import { Document, Workflow } from "../interfaces";
import ErrorModal from "./ErrorModal";
import useSWR from "swr";
import { useHistory } from "react-router-dom";
import { useState } from "react";
import { Popover } from "@headlessui/react";
import moment from "moment-timezone";
import CopyToClipboard from "react-copy-to-clipboard";
import PayWallModalTeams from "./PayWallModalTeams";
import { SIGNATURES_ACTIVITY_MAP, SIGNATURES_STATUS_MAP } from "../utils";

interface Props {
  contentOffset: number,
  createNewWorkflow: (stage: "SOURCE" | "DOCUMENT", blockType: string, file?: any, isPdf?: boolean, options?: Record<string, any>) => void;
}

function SignaturesHome(props: Props) {
  const { t } = useTranslation();
  const { openModal, closeModal } = useModal();

  const history = useHistory();

  const { user, hasPermission } = useUser();

  const { data, mutate, isValidating } = useSWR<{ "numPages": number, "total": number, "results": Array<Workflow> }>("/signatures/workflows/", getData);
  const { results: signatureWorkflows } = data ?? {};

  const [isRefreshing, setIsRefreshing] = useState(false);
  const [currentWorkflowId, setCurrentWorkflowId] = useState<string>();

  const [copiedSignatureRequest, setCopiedSignatureRequest] = useState<number>();

  const [cancellingItem, setCancellingItem] = useState<string>();
  const [archivingWorkflow, setArchivingWorkflow] = useState<string>();

  function handleOnCopiedSignatureRequest(recipient: number) {
    setCopiedSignatureRequest(recipient);
    setTimeout(() => setCopiedSignatureRequest(undefined), 2000);
  }

  async function onFileUpload(file: any) {
    const initialWorkflow = {
      name: file["name"] || t("Signature Workflow"),
      ...{ "color": "#cccccc", "icon": "DocumentText" }
    }

    const workflow = await postData("/workflows/", initialWorkflow);
    if (!!user?.activeTeam)
      postData(`/workflows/${workflow.id}/team/`)
        .catch(console.error)

    try {
      openModal(
        <LoadingModal
          title={t("Creating Workflow")}
          subtitle={t("This may take a minute as we create a new file in your Drive.")}
        />
      )

      // TODO: Can these all be done in the backend with one request?
      await postData(`/workflows/${workflow.id}/source/`, { sourceType: "SIGNATURES" });
      await postData(`/workflows/${workflow.id}/signature-config/`, { sendConfirmationEmail: true });

      const document: Document = await postData(`/workflows/${workflow.id}/documents/`, { documentType: "PDF_FILLABLE", isSignable: true });

      const content = new Blob([file], { type: "application/pdf" });

      const { url } = await postData(`/workflows/documents/${document!.id}/generate-pdf-upload-url/`);

      await fetch(url, {
        method: "PUT",
        headers: {
          "content-type": "application/pdf"
        },
        body: content
      });

      await postData(`/workflows/documents/${document!.id}/confirm-pdf-upload-status/`, { filename: file.name });

      closeModal();
      history.push("/s/" + workflow.id);

    } catch (error) {
      openModal(<ErrorModal details={error} />)
    }
  }

  function handleUpload() {
    if (!hasPermission(user?.activeTeam, "workflows.create"))
      return;

    openModal(<FileUploadModal onUpload={onFileUpload} allowedFormats={["PDF"]} />);
  }

  function handleAddWorkflow() {
    if (!hasPermission(user?.activeTeam, "workflows.create"))
      return;

    openModal(<WorkflowSelectModal title={t("New Workflow")} subtitle={t("Select a source or template document to start building a workflow")} showDocuments makeSignable onSelection={props.createNewWorkflow} />);
  }

  function handleClickRefresh() {
    setIsRefreshing(true);
    mutate()
      .finally(() => setIsRefreshing(false));
  }

  function handleAddToTeam(workflowId: string) {
    if (!user?.subscription?.featureTeams)
      openModal(<PayWallModalTeams />);

    postData(`/workflows/${workflowId}/team/`)
      .catch(console.error)
  }

  function handleRemoveFromTeam(workflowId: string) {
    deleteData(`/workflows/${workflowId}/team/`)
      .catch(console.error)
  }

  function handleCancelItem(automationItemId: string) {
    setCancellingItem(automationItemId)

    postData(`/workflows/automation-items/${automationItemId}/rescind/`)
      .then(() => mutate())
      .catch(console.error)
      .finally(() => setCancellingItem(undefined));
  }

  function handleArchiveWorkflow(workflowId: string) {
    setArchivingWorkflow(workflowId)

    deleteData(`/workflows/${workflowId}/`)
      .then(() => mutate())
      .catch(console.error)
      .finally(() => setArchivingWorkflow(undefined));
  }

  function handleDownloadItem(workflowId: string) {
    getData(`/signatures/document-url/${workflowId}/`)
      .then(({ url }) => window.open(url, "_blank"))
      .catch(console.error)
  }

  return (
    <div className="flex flex-col border border-gray-200 rounded-lg bg-white p-4 w-full h-full gap-10">

      <div className="flex flex-col gap-2">
        <div className="font-semibold test-gray-400">
          New Signatures
        </div>
        <div className="flex items-center gap-4">
          <div className="border border-gray-200 rounded-lg cursor-pointer learn-card-hover transition-all w-full h-[200px] relative overflow-hidden"
            onClick={handleUpload}
          >
            {/* Background SVG behind content */}
            <img src={learnBg1} alt="" className="absolute inset-0 w-full h-full object-cover" />

            <div className="relative z-10 p-4 h-full flex flex-col">
              <div className="w-8 h-8 rounded mb-4 flex items-center justify-center bg-lightblue">
                <DocumentTextIcon className="w-6 h-6 text-blue" />
              </div>
              <h3 className="text-black font-gilroy font-semibold mb-2 text-3xl leading-8">
                {t("Upload a Document")}
              </h3>
              <p className="text-gray-600 font-gilroy text-xs leading-4">
                {t("Add signature fields to an existing PDF document and send for signing")}
              </p>
              <button className="btn btn-blue text-sm w-52 mt-auto gap-2" disabled={!hasPermission(user?.activeTeam, "workflows.create")}>
                <UploadIcon className="w-4 h-4" />
                Upload
              </button>
            </div>
          </div>

          <div className="border border-gray-200 rounded-lg cursor-pointer learn-card-hover transition-all w-full h-[200px] relative overflow-hidden"
            onClick={handleAddWorkflow}
          >
            {/* Background SVG behind content */}
            <img src={learnBg1} alt="" className="absolute inset-0 w-full h-full object-cover" />

            <div className="relative z-10 p-4 h-full flex flex-col">
              <div className="w-8 h-8 rounded mb-4 flex items-center justify-center bg-lightblue">
                <LightningBoltIcon className="w-6 h-6 text-blue" />
              </div>
              <h3 className="text-black font-gilroy font-semibold mb-2 text-3xl leading-8">
                {t("Create a Signature Workflow")}
              </h3>
              <p className="text-gray-600 font-gilroy text-xs leading-4">
                {t("Build a custom workflow to capture signatures")}
              </p>
              <button className="btn btn-white text-sm w-52 mt-auto gap-2" disabled={!hasPermission(user?.activeTeam, "workflows.create")}>
                <PlusIcon className="w-4 h-4" />
                New Workflow
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-2 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="font-semibold test-gray-400">
            Activity
          </div>
          <button className="flex items-center gap-1 font-semibold font-gilroy text-gray-600 hover:text-blue hover:underline cursor-pointer" onClick={handleClickRefresh}>
            <RefreshIcon className={`h-5 w-5 ${(isRefreshing || isValidating) ? "animate-spin" : "animate-none"}`} />
          </button>
        </div>
        <div className="flex flex-col border rounded overflow-hidden">
          <div className="flex items-center bg-gray-100 p-2 font-semibold text-gray-600 text-sm">
            <div className="w-10 flex-shrink-0"></div>
            <div className="w-full">Filename</div>
            <div className="w-40 flex-shrink-0 text-left">{t("Status")}</div>
            <div className="w-52 flex-shrink-0 text-left">{t("Latest Activity")}</div>
            <div className="w-40 flex-shrink-0 text-left">{t("Sender")}</div>
            <div className="w-32 flex-shrink-0 text-left">{t("Expiry")}</div>
            <div className="w-32 flex-shrink-0 text-left"><FilterIcon className="w-6 h-6 flex-shrink-0 ml-auto text-gray-600 invisible" /></div>
          </div>
          <div className="flex flex-col overflow-y-scroll scrollbar-hidden" style={{ height: "calc(100vh - 464px)" }}>
            {signatureWorkflows?.length === 0 &&
              <div className="m-auto font-semibold text-gray-600 text-sm">
                {t("No existing signature requests, upload a PDF to get signed")}
              </div>
            }
            {signatureWorkflows?.map((workflow: Workflow) => (
              <div key={workflow.id} className="flex flex-col p-2">
                <div className="flex py-2 text-sm items-center text-gray-600">
                  <div className="w-10 flex-shrink-0 flex items-center justify-between pr-1">
                    <ChevronRightIcon className={`w-6 h-6 flex-shrink-0 cursor-pointer transition-transform ${currentWorkflowId === workflow.id ? "rotate-90" : "rotate-0"} ${workflow.derivedStatus !== "DRAFT" ? "visible" : "invisible"}`} onClick={() => setCurrentWorkflowId(curr => curr === workflow.id ? undefined : workflow.id)} />
                    <div className="bg-green w-2 h-2 rounded-full invisible" />
                  </div>
                  <div className="w-full" onClick={() => history.push("/s/" + workflow.id)}>
                    <div className="w-72 truncate cursor-pointer font-semibold">
                      {workflow.name}
                    </div>
                  </div>
                  <div className="w-40 truncate flex-shrink-0 text-left">
                    {((!workflow.primaryItem || cancellingItem !== workflow.primaryItem?.id) && archivingWorkflow !== workflow.id) &&
                      <div className={`w-fit px-4 font-semibold rounded-full cursor-pointer border ${SIGNATURES_STATUS_MAP[workflow.derivedStatus ?? ""]?.[1]}`} onClick={() => history.push("/s/" + workflow.id)}>
                        {SIGNATURES_STATUS_MAP[workflow.derivedStatus ?? ""]?.[0]}
                      </div>
                    }
                    {(!!workflow.primaryItem && cancellingItem === workflow.primaryItem?.id && archivingWorkflow !== workflow.id) &&
                      <div className={`w-fit px-4 font-semibold rounded-full cursor-pointer border ${SIGNATURES_STATUS_MAP["CANCELED"]?.[1]}`} onClick={() => history.push("/s/" + workflow.id)}>
                        Cancelling...
                      </div>
                    }
                    {(archivingWorkflow === workflow.id) &&
                      <div className={`w-fit px-4 font-semibold rounded-full cursor-pointer border ${SIGNATURES_STATUS_MAP["DRAFT"]?.[1]}`} onClick={() => history.push("/s/" + workflow.id)}>
                        Archiving...
                      </div>
                    }
                  </div>
                  <div className="w-52 truncate flex-shrink-0 text-left">
                    {SIGNATURES_ACTIVITY_MAP[workflow.derivedActivity?.eventType ?? ""]} {moment(workflow.derivedActivity?.createdAt ?? workflow.updatedAt).fromNow()}
                  </div>
                  <div className="w-40 truncate flex-shrink-0 text-left">{workflow.owner?.fullName}</div>
                  <div className="w-32 truncate flex-shrink-0 text-left">{moment(workflow.derivedExpiry).format("DD MMM YYYY")}</div>
                  <div className="w-32  flex-shrink-0 text-left">
                    <Popover className="flex flex-col relative">
                      <Popover.Button className="flex items-center justify-center ml-auto text-sm border rounded bg-gray-100 w-20">
                        {t("Actions")}
                        <ChevronDownIcon className="w-4 h-4 ml-1" />
                      </Popover.Button>
                      <Popover.Panel className="flex flex-col absolute bg-white border right-0 top-8 z-40 w-fit text-nowrap shadow text-sm rounded">
                        {workflow.primaryItem &&
                          <CopyToClipboard text={workflow.primaryItem.id}>
                            <Popover.Button className="text-left py-1 px-2 hover:bg-blue-50">
                              {t("Copy support code")}
                            </Popover.Button>
                          </CopyToClipboard>
                        }
                        {!workflow.team &&
                          <Popover.Button className="text-left py-1 px-2 hover:bg-blue-50" onClick={() => handleAddToTeam(workflow.id)}>
                            {t("Share with team")}
                          </Popover.Button>
                        }
                        {!!workflow.team &&
                          <Popover.Button className="text-left py-1 px-2 hover:bg-blue-50" onClick={() => handleRemoveFromTeam(workflow.id)}>
                            {t("Hide from team")}
                          </Popover.Button>
                        }
                        <Popover.Button className="text-left py-1 px-2 hover:bg-blue-50" onClick={() => history.push("/s/" + workflow.id)}>
                          {t("View document")}
                        </Popover.Button>
                        <Popover.Button className="text-left py-1 px-2 hover:bg-blue-50" onClick={() => history.push("/w/" + workflow.id)}>
                          {t("View workflow")}
                        </Popover.Button>
                        {(!!workflow.primaryItem && ["REQUESTED", "PARTIALLY_SIGNED"].includes(workflow.derivedStatus ?? "")) &&
                          <Popover.Button className="text-left py-1 px-2 hover:bg-blue-50" onClick={() => handleCancelItem(workflow.primaryItem!.id)}>
                            {t("Cancel requests")}
                          </Popover.Button>
                        }
                        {(!!workflow.primaryItem && ["COMPLETED"].includes(workflow.derivedStatus ?? "")) &&
                          <Popover.Button className="text-left py-1 px-2 hover:bg-blue-50" onClick={() => handleDownloadItem(workflow.id)}>
                            {t("Download")}
                          </Popover.Button>
                        }
                        <Popover.Button className="text-left py-1 px-2 hover:bg-blue-50" onClick={() => handleArchiveWorkflow(workflow.id)}>
                          {t("Archive")}
                        </Popover.Button>
                      </Popover.Panel>
                    </Popover>
                  </div>
                </div>
                {currentWorkflowId === workflow.id &&
                  <div className="flex flex-col bg-gray-100 w-full border-t shadow-inner rounded-b py-2">
                    <div className="flex items-center w-full">
                      <div className="w-10 flex-shrink-0 text-left bg-purple/50"></div>
                      {(workflow.primaryItem?.signatureRequests.length ?? 0) > 0 &&
                        <div className="w-full text-xs font-semibold text-gray-400">Recipients</div>
                      }
                    </div>
                    <div className="flex flex-col">
                      {workflow.primaryItem?.signatureRequests?.map((sr) =>
                        <div key={sr.id} className="flex items-center w-full py-1 text-sm text-gray-600">
                          <div className="w-10 flex-shrink-0 text-left"></div>
                          <div className="w-full text-left font-semibold">{sr.recipientEmail}</div>
                          <div className="w-40 flex-shrink-0 text-left">
                            <div className={`w-fit px-4 font-semibold rounded-full border ${SIGNATURES_STATUS_MAP[sr.status ?? ""]?.[1]}`}>
                              {SIGNATURES_STATUS_MAP[sr.status ?? ""]?.[0]}
                            </div>
                          </div>
                          <div className="w-40 flex-shrink-0 text-left truncate">
                            {sr.events[0] &&
                              <>{SIGNATURES_ACTIVITY_MAP[sr.events[0]?.eventType ?? ""]} {moment(sr.events[0]?.createdAt).fromNow()}</>
                            }
                          </div>
                          <div className="w-52 flex-shrink-0 text-left">
                          </div>
                          <div className="w-32 flex-shrink-0 text-left"></div>
                          <div className="w-32 flex-shrink-0 text-left">
                            {(sr.recipientNumber > 0 && (sr.status === "REQUESTED" || sr.status === "ACCESSED")) &&
                              <div className="flex items-center">
                                {/* <div className="text-xs text-gray-600">{t("Link")}:</div> */}
                                <a className="flex items-center gap-1 text-xs text-gray-600 ml-2 hover:underline cursor-pointer -mt-1" target={sr.publicTokenUrl} href={sr.publicTokenUrl} rel="noreferrer">
                                  <ExternalLinkIcon className="w-4 h-4 flex-shrink-0" /> {t("Open")}
                                </a>
                                <CopyToClipboard text={sr.publicTokenUrl} onCopy={() => handleOnCopiedSignatureRequest(sr.recipientNumber)}>
                                  {copiedSignatureRequest === sr.recipientNumber
                                    ? <div className="flex items-center gap-1 text-xs text-blue ml-2 font-semibold select-none -mt-1"><ClipboardCopyIcon className="w-4 h-4 flex-shrink-0" /> {t("Copied")}</div>
                                    : <div className="flex items-center gap-1 text-xs text-gray-600 ml-2 hover:underline cursor-pointer -mt-1"><ClipboardCopyIcon className="w-4 h-4 flex-shrink-0" /> {t("Copy")}</div>
                                  }
                                </CopyToClipboard>
                              </div>
                            }
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                }
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default SignaturesHome;
